import React from 'react'

/* Atoms */
import Box from 'components_new/atoms/Box'

/* Organisms */
import BarChart from 'components_new/organisms/Widget/Types/BarChart'
import BubbleChart from 'components_new/organisms/Widget/Types/BubbleChart'
import ChartDataTable from 'components_new/organisms/Widget/Types/ChartDataTable'
import ComboChart from 'components_new/organisms/Widget/Types/ComboChart'
import DashboardWidgetTable from 'components_new/organisms/Widget/Types/Table/DashboardWidgetTable'
import Funnel from 'components_new/organisms/Widget/Types/Funnel'
import KeyFigure from 'components_new/organisms/Widget/Types/KeyFigure'
import LineChart from 'components_new/organisms/Widget/Types/LineChart'
import Map from 'components_new/organisms/Widget/Types/Map'
import PieChart from 'components_new/organisms/Widget/Types/PieChart'

import { getIsComparativeButIllegal, getSegmentSetters } from './utils'

import { useSnackbarActions } from 'redux/actions/Snackbar'
import { SnackbarMessage } from 'types/GlobalSnackbar'

/* Types */
import { DashboardVariantType } from 'types/GlobalDashboard'
import { DashboardFilter } from 'types/GlobalDashboardFilter'
import {
  CustomPeriodFilter,
  FormattedWidgetData,
  PeriodFilter,
  ParsedSegmentPath,
  ParsedWidgetDataLabel,
  TViewport,
  WidgetOrder,
  WidgetType,
  TCoreWidgetFullyLoaded
} from 'types/GlobalWidget'

interface DashboardWidgetTypeProps {
  customSegmentBy: string | null
  dashboardFilter: DashboardFilter
  fullSize: boolean
  embedded: boolean
  formattedData: FormattedWidgetData
  interactive: boolean
  isAdmin: boolean
  onKeyFigureClick?: (event: React.MouseEvent, kpiOptionId: string) => void
  onSegmentClick?: (
    event: React.MouseEvent<HTMLElement, MouseEvent> | PointerEvent,
    segments: ParsedWidgetDataLabel[],
    datasetIndex?: number
  ) => void
  order: WidgetOrder
  periodFilter: PeriodFilter | null
  scaleFactor: number
  setCustomSegmentBy: (
    attributeOptionId: string,
    dbFilter: DashboardFilter,
    periodFilter?: CustomPeriodFilter
  ) => void
  setDashboardFilter: (
    dbFilter: DashboardFilter,
    periodFilter?: CustomPeriodFilter
  ) => void
  setOrder: (order: WidgetOrder) => void
  setViewport: (value: TViewport) => void
  standaloneWidget: boolean
  variantType: DashboardVariantType
  viewport: TViewport
  widget: TCoreWidgetFullyLoaded
  showUnderlyingContent: (
    segmentPaths: ParsedSegmentPath[],
    initialKpiOptionId: string | null
  ) => void
}

const DashboardWidgetType = (props: DashboardWidgetTypeProps) => {
  const { toggleSnackbarOpen } = useSnackbarActions()

  const {
    customSegmentBy,
    dashboardFilter,
    embedded,
    formattedData,
    fullSize,
    interactive,
    onKeyFigureClick,
    onSegmentClick,
    order,
    periodFilter,
    scaleFactor,
    setCustomSegmentBy,
    setDashboardFilter,
    setOrder,
    setViewport,
    showUnderlyingContent,
    standaloneWidget,
    variantType,
    viewport,
    widget
  } = props

  const isComparativeButIllegal = getIsComparativeButIllegal(
    periodFilter ? periodFilter : widget.settings.date_filter.selected?.value,
    widget.settings.comparative_period.selected?.value,
    widget.settings.date_filter.type
  )
  const segmentSetterFunctions = getSegmentSetters(
    widget,
    dashboardFilter,
    setDashboardFilter,
    showUnderlyingContent,
    setCustomSegmentBy,
    standaloneWidget,
    interactive,
    variantType,
    () => toggleSnackbarOpen(SnackbarMessage.FileDownloadFailed)
  )

  const isCanvasChart = [
    WidgetType.BAR_CHART,
    WidgetType.BUBBLE_CHART,
    WidgetType.COMBO_CHART,
    WidgetType.LINE_CHART,
    WidgetType.PIE_CHART
  ].includes(widget.settings.type.selected)

  const renderWidget = () => {
    switch (widget.settings.type.selected) {
    case WidgetType.BAR_CHART:
      return (
        <BarChart
          formattedData={formattedData}
          isComparativeButIllegal={isComparativeButIllegal}
          onSegmentClick={onSegmentClick}
          scaleFactor={scaleFactor}
          customSegmentBy={customSegmentBy}
          widget={widget}
          {...segmentSetterFunctions}
        />
      )
    case WidgetType.BUBBLE_CHART:
      return (
        <BubbleChart
          formattedData={formattedData}
          onSegmentClick={onSegmentClick}
          scaleFactor={scaleFactor}
          customSegmentBy={customSegmentBy}
          widget={widget}
          {...segmentSetterFunctions}
        />
      )
    case WidgetType.KEY_FIGURE:
      return (
        <KeyFigure
          interactive={interactive}
          scaleFactor={scaleFactor}
          widget={widget}
          setDetailsDialog={(initialId: string | null) =>
            showUnderlyingContent([], initialId)
          }
          onKpiClick={onKeyFigureClick}
        />
      )
    case WidgetType.COMMENT:
      return null
    case WidgetType.COMBO_CHART:
      return (
        <ComboChart
          formattedData={formattedData}
          isComparativeButIllegal={isComparativeButIllegal}
          onSegmentClick={onSegmentClick}
          scaleFactor={scaleFactor}
          customSegmentBy={customSegmentBy}
          widget={widget}
          {...segmentSetterFunctions}
        />
      )
    case WidgetType.LINE_CHART:
      return (
        <LineChart
          formattedData={formattedData}
          onSegmentClick={onSegmentClick}
          scaleFactor={scaleFactor}
          widget={widget}
          {...segmentSetterFunctions}
        />
      )
    case WidgetType.LIST:
      return (
        <DashboardWidgetTable
          embedded={embedded}
          formattedData={formattedData}
          isComparativeButIllegal={isComparativeButIllegal}
          onSegmentClick={onSegmentClick}
          scaleFactor={scaleFactor}
          customSegmentBy={customSegmentBy}
          order={order}
          setOrder={setOrder}
          widget={widget}
          {...segmentSetterFunctions}
        />
      )
    case WidgetType.MAP:
      return (
        <Map
          formattedData={formattedData}
          isComparativeButIllegal={isComparativeButIllegal}
          scaleFactor={scaleFactor}
          setViewport={setViewport}
          viewport={viewport}
          widget={widget}
          {...segmentSetterFunctions}
        />
      )
    case WidgetType.FUNNEL:
      return (
        <Funnel
          clipContent={fullSize}
          embedded={embedded}
          scaleFactor={scaleFactor}
          widget={widget}
          {...segmentSetterFunctions}
        />
      )
    case WidgetType.PIE_CHART:
      return (
        <PieChart
          formattedData={formattedData}
          onSegmentClick={onSegmentClick}
          scaleFactor={scaleFactor}
          customSegmentBy={customSegmentBy}
          widget={widget}
          {...segmentSetterFunctions}
        />
      )
    default:
      return null
    }
  }

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        userSelect: !fullSize ? 'none' : undefined,
        cursor: !fullSize ? 'default' : undefined
      }}
    >
      {renderWidget()}
      {isCanvasChart ? (
        <ChartDataTable formattedData={formattedData} title={widget.title} />
      ) : null}
    </Box>
  )
}

export default DashboardWidgetType
